import React, { Ref } from 'react';
import { useResetCache } from './impl/useResetCache';
import { OuterElementContext, OuterElementType } from './impl/OuterElementContext';
import { ListChildComponentProps, ListItemKeySelector, VariableSizeList, areEqual } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import { BpDesignSystem } from '../../types';
import { Box } from '../Box';

export interface VirtualListItemComponentProps<T extends object, P extends object> {
  /* can be undefined when dragging */
  item?: T;
  /* can be undefined when dragging */
  props?: P;
  /* It must be passed to the topmost HTML element in your component. */
  style: React.CSSProperties;
  index: number;
  isScrolling?: boolean;
}

type VirtualListProps<T extends object, P extends object> = BpDesignSystem.Props<{
  data: {
    items: T[];
    props: P;
  };
  component: React.FC<VirtualListItemComponentProps<T, P>>;
  itemSize?: number;
  itemCount?: number;
  itemKey: ListItemKeySelector<{ items: T[]; props: P }>;
  overscanCount?: number;
  outerRef?: Ref<any> | undefined;
}>;

const LISTBOX_PADDING = 0; // px

export function VirtualList<T extends object, P extends object = {}>({
  data,
  component,
  itemSize = 42,
  itemCount,
  itemKey,
  overscanCount = 5,
  outerRef,
  ...bpProps
}: VirtualListProps<T, P>) {
  const targetItemCount = itemCount ?? data.items.length;
  const gridRef = useResetCache(targetItemCount);

  return (
    <Box {...bpProps} style={{ height: '100%', ...bpProps.style }}>
      <AutoSizer>
        {({ width, height }) => (
          <OuterElementContext.Provider
            value={{
              component
            }}
          >
            <VariableSizeList
              itemData={data}
              height={height}
              width={width}
              ref={gridRef}
              outerRef={outerRef}
              outerElementType={OuterElementType}
              innerElementType="ul"
              itemSize={() => itemSize}
              overscanCount={overscanCount}
              itemCount={targetItemCount}
              itemKey={itemKey}
              useIsScrolling
            >
              {renderRow}
            </VariableSizeList>
          </OuterElementContext.Provider>
        )}
      </AutoSizer>
    </Box>
  );
}

const renderRow = React.memo(
  ({ style, data, index, isScrolling }: ListChildComponentProps<VirtualListProps<object, object>['data']>) => {
    const componentProps = data?.props;
    const componentData = data?.items[index];
    const contextProps = React.useContext(OuterElementContext);
    const inlineStyle = {
      ...style,
      top: (style.top as number) + LISTBOX_PADDING,
      overflow: 'hidden'
    } satisfies React.CSSProperties;

    const Component = 'component' in contextProps ? contextProps.component : undefined;

    if (Component && isFC(Component)) {
      return (
        <Component
          item={componentData}
          props={componentProps}
          style={inlineStyle}
          index={index}
          isScrolling={isScrolling}
        />
      );
    }
    return null;
  },
  areEqual
);

function isFC(component: unknown): component is VirtualListProps<any, any>['component'] {
  return typeof component === 'function';
}
